import { useRef, useCallback, useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-fade";

import { EffectFade, Autoplay } from "swiper/modules";
import Button from "blocks/Button";

const HERO_BASE = "/images/secciones/inicio/Hero%20Carousel%20Inicio%20principal";
const heroVideo1 = `${HERO_BASE}/hero-slide1.mp4`;
// Primer fotograma del video: se pinta al instante mientras el mp4 descarga.
const heroVideo1Poster = `${HERO_BASE}/hero-slide1-poster.webp`;
const heroImage2 = `${HERO_BASE}/Physiology_skincare_site_inicio_carousel_slide_2.webp`;
const heroImage3 = `${HERO_BASE}/Physiology_skincare_site_inicio_carousel_slide_3.webp`;

// Data de los slides
const slides = [
  {
    miniTitle: "Cacería de Brujas",
    title: "Hechizos de hasta -30% para tu piel",
    description: "Esta temporada conjuramos nuestras mejores ofertas: descuentos de hasta -30% en tus fórmulas favoritas. Un ritual de luminosidad por tiempo limitado.",
    buttonText: "Explorar la Cacería de Brujas",
    buttonLink: "/shop",
    alt: "Productos Physiology",
    extraImage: null,
    bgColor: "#c8c8c8",
    mediaType: "video",
    mediaSrc: heroVideo1,
    poster: heroVideo1Poster
  },
  {
    miniTitle: "Equilibrio entre frescura y serenidad",
    title: "Sensaciones naturales y olores boscosos",
    description: "Con ingredientes naturales sin alcohol, calma rojeces e irritaciones, ideal para pieles sensibles en la rutina diaria. Hidrata tu piel y cuídala.",
    buttonText: "Descubre cada esencia",
    buttonLink: "/shop?category=tonicos",
    alt: "Body and hand care products",
    extraImage: null,
    bgColor: "#a3a3a3",
    mediaType: "image",
    mediaSrc: heroImage2
  },
  {
    miniTitle: "La sencillez no es ausencia",
    title: "Perfecto minimalismo en nuestras fórmulas",
    description: "En cada textura hay una decisión consciente: Percibir, no distraer. El cuidado comienza cuando entendemos que lo suficiente es, en realidad, todo.",
    buttonText: "Conoce la formulación",
    buttonLink: "/ingredientes",
    alt: "Vitamina C Serum",
    extraImage: null,
    bgColor: "#c2c8ca",
    mediaType: "image",
    mediaSrc: heroImage3
  }
];

// El contorno gris por defecto (#a3a3a3) desaparece sobre los fondos grises del slider.
const HERO_BTN_BORDER = 'rgba(28, 43, 58, 0.45)';

const SectionOne = () => {
  const [pauseBtn, setPause] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [mounted, setMounted] = useState(false);
  const sliderRef = useRef(null);

  // Entrada unificada: texto y media aparecen juntos. Esperamos a que la media
  // del primer slide (poster o imagen) esté lista para no revelar solo la mitad
  // del texto con el hueco de la foto/video vacío. Fallback por si tarda.
  useEffect(() => {
    let done = false;
    const reveal = () => {
      if (done) return;
      done = true;
      requestAnimationFrame(() => setMounted(true));
    };
    const first = slides[0];
    const img = new Image();
    img.onload = reveal;
    img.onerror = reveal;
    img.src = first.poster || first.mediaSrc;
    const fallback = setTimeout(reveal, 1500);
    return () => {
      done = true;
      clearTimeout(fallback);
    };
  }, []);

  useEffect(() => {
    const swiperInstance = sliderRef.current?.swiper;
    if (!swiperInstance) return;

    const handleSlideChange = () => {
      setCurrentSlide(swiperInstance.realIndex);
    };

    swiperInstance.on("slideChange", handleSlideChange);
    return () => swiperInstance.off("slideChange", handleSlideChange);
  }, []);

  const prevSlide = useCallback(() => {
    sliderRef.current?.swiper.slidePrev();
  }, []);

  const nextSlide = useCallback(() => {
    sliderRef.current?.swiper.slideNext();
  }, []);

  const toggleAutoplay = useCallback(() => {
    const swiper = sliderRef.current?.swiper;
    if (!swiper) return;
    
    if (pauseBtn) {
      swiper.autoplay.start();
    } else {
      swiper.autoplay.stop();
    }
    setPause(!pauseBtn);
  }, [pauseBtn]);

  // Componente de controles
  const SliderControls = () => (
  <div className="flex items-center justify-center gap-x-6 py-4">
    <button 
      className="cursor-pointer hover:opacity-60 transition-opacity p-1" 
      onClick={prevSlide}
      aria-label="Previous slide"
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--phys-ink)" strokeWidth="1.5">
        <polyline points="15 18 9 12 15 6"></polyline>
      </svg>
    </button>

    <span className="text-sm text-offBlack min-w-[45px] text-center">
      {currentSlide + 1} / {slides.length}
    </span>

    <button 
      className="cursor-pointer hover:opacity-60 transition-opacity p-1" 
      onClick={nextSlide}
      aria-label="Next slide"
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--phys-ink)" strokeWidth="1.5">
        <polyline points="9 18 15 12 9 6"></polyline>
      </svg>
    </button>

    <button 
  className="cursor-pointer hover:opacity-60 transition-opacity p-1" 
  onClick={toggleAutoplay}
  aria-label={pauseBtn ? "Play" : "Pause"}
>
  {pauseBtn ? (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="var(--phys-ink)" stroke="none">
      <polygon points="5 3 19 12 5 21 5 3"></polygon>
    </svg>
  ) : (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="var(--phys-ink)" stroke="none">
      <rect x="6" y="4" width="2" height="16"></rect>
      <rect x="14" y="4" width="2" height="16"></rect>
    </svg>
  )}
</button>
  </div>
);

  return (
    <div className={`relative hero-section${mounted ? ' hero-section--entered' : ''}`}>
      <Swiper
        ref={sliderRef}
        loop={true}
        effect="fade"
        autoplay={{
          delay: 6000,
          disableOnInteraction: false,
        }}
        modules={[EffectFade, Autoplay]}
        className="heroSwiper"
      >
        {slides.map((slide, index) => (
          <SwiperSlide key={index}>
            <section className="relative">
              <div className="lg:grid flex flex-col grid-cols-2 relative">
                
                {/* Imagen / Video */}
                <div className="relative lg:order-2">
                  {slide.mediaType === 'video' ? (
                    <video 
                      className="h-[40vh] lg:h-[45vh] w-full object-cover"
                      style={{ backgroundColor: slide.bgColor }}
                      poster={slide.poster}
                      autoPlay
                      loop
                      muted
                      playsInline
                      preload="auto"
                    >
                      <source src={slide.mediaSrc} type="video/mp4" />
                    </video>
                  ) : (
                    <img
                      src={slide.mediaSrc}
                      alt={slide.alt}
                      className="h-[40vh] lg:h-[45vh] w-full object-cover"
                      style={{ backgroundColor: slide.bgColor }}
                      loading={index === 0 ? "eager" : "lazy"}
                    />
                  )}
                  {slide.extraImage && (
                    <div className="w-[35%] absolute right-4 -bottom-2 lg:bottom-16">
                      <img 
                        src={slide.extraImage} 
                        alt="Featured products" 
                        className="w-full h-auto object-contain"
                      />
                    </div>
                  )}
                </div>

                {/* Mobile: Controles + Texto con color dinámico */}
                {/* Desktop: Solo texto con color dinámico, controles separados */}
                
                {/* Mobile: Todo el contenido con color */}
                <div 
                  className="lg:hidden"
                  style={{ backgroundColor: slide.bgColor }}
                >
                  <SliderControls />
                  <div className="w-full px-8 pb-10">
                    <header className="flex flex-col">
                      <h3 className="text-sm leading-7 mb-2">{slide.miniTitle}</h3>
                      <h2 className="text-2xl mb-3">{slide.title}</h2>
                      <p className="text-sm leading-7 mb-4">{slide.description}</p>
                      <Button btn={slide.buttonText} href={slide.buttonLink} borderColor={HERO_BTN_BORDER} />
                    </header>
                  </div>
                </div>

                {/* Desktop: Solo texto con color */}
                <div 
                  className="hidden lg:flex lg:order-1 lg:h-[45vh] items-center justify-center"
                  style={{ backgroundColor: slide.bgColor }}
                >
                  <div className="w-full max-w-lg xl:max-w-xl px-12">
                    <header className="flex flex-col">
                      <h3 className="text-sm leading-7 mb-2">{slide.miniTitle}</h3>
                      <h2 className="text-3xl mb-3">{slide.title}</h2>
                      <p className="text-base leading-7 mb-4">{slide.description}</p>
                      <Button btn={slide.buttonText} href={slide.buttonLink} borderColor={HERO_BTN_BORDER} />
                    </header>
                  </div>
                </div>

              </div>
            </section>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Desktop: Controles con fondo neutro */}
      <div className="hidden lg:block bg-offWhite">
        <SliderControls />
      </div>
    </div>
  );
};

export default SectionOne;
